import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from "recharts";
import { TrendingUp, DollarSign, Users, Truck, Calendar, FileText, Package, Activity, AlertTriangle, MapPin } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import BackNavigation from "../components/BackNavigation";

const Dashboard = () => {
  const { projectId } = useParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [projectId]);

  const fetchDashboardData = async () => {
    try {
      const response = await apiClient.get(`/dashboard/${projectId}`);
      setDashboardData(response.data);
    } catch (error) {
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading dashboard...</div>;
  }

  if (!dashboardData) {
    return <div className="text-center py-12">No data available</div>;
  }

  const { project, progress_percentage, total_budget, spent_amount, remaining_budget, avg_workforce, equipment_hours, total_materials, recent_dprs, progress_data } = dashboardData;

  return (
    <div>
      <BackNavigation title="Dashboard" projectName={project?.name} />
      
      <div className="mb-8">
        <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">{project.name}</h1>
        <p className="text-slate-600 mt-2 font-body flex items-center">
          <MapPin className="w-4 h-4 mr-2" />
          {project.location}
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest text-slate-600 font-medium">Progress</p>
            <TrendingUp className="w-5 h-5 text-success" />
          </div>
          <p className="font-heading font-black text-4xl text-primary font-mono" data-testid="progress-percentage">{progress_percentage.toFixed(1)}%</p>
          <div className="mt-3 bg-slate-200 h-2 rounded-full overflow-hidden">
            <div className="bg-success h-full" style={{ width: `${progress_percentage}%` }}></div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest text-slate-600 font-medium">Budget</p>
            <DollarSign className="w-5 h-5 text-accent" />
          </div>
          <p className="font-heading font-black text-3xl text-primary font-mono" data-testid="total-budget">₹{(total_budget / 10000000).toFixed(2)} Cr</p>
          <p className="text-sm text-slate-600 mt-1">Spent: <span className="font-mono font-semibold">₹{(spent_amount / 10000000).toFixed(2)} Cr</span></p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest text-slate-600 font-medium">Workforce</p>
            <Users className="w-5 h-5 text-primary" />
          </div>
          <p className="font-heading font-black text-4xl text-primary font-mono" data-testid="avg-workforce">{Math.round(avg_workforce)}</p>
          <p className="text-sm text-slate-600 mt-1">Average Daily</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest text-slate-600 font-medium">Equipment Hours</p>
            <Truck className="w-5 h-5 text-warning" />
          </div>
          <p className="font-heading font-black text-4xl text-primary font-mono" data-testid="equipment-hours">{equipment_hours.toFixed(0)}</p>
          <p className="text-sm text-slate-600 mt-1">Total Usage</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Link to={`/boq/${projectId}`} data-testid="nav-boq" className="stat-card hover:shadow-lg transition-shadow cursor-pointer group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Bill of Quantities</p>
              <p className="font-heading font-bold text-2xl text-primary group-hover:text-accent transition-colors">Manage BOQ</p>
            </div>
            <FileText className="w-10 h-10 text-accent" />
          </div>
        </Link>

        <Link to={`/dpr/${projectId}`} data-testid="nav-dpr-entry" className="stat-card hover:shadow-lg transition-shadow cursor-pointer group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Daily Progress Report</p>
              <p className="font-heading font-bold text-2xl text-primary group-hover:text-accent transition-colors">Enter DPR</p>
            </div>
            <Calendar className="w-10 h-10 text-success" />
          </div>
        </Link>

        <Link to={`/materials/${projectId}`} data-testid="nav-materials" className="stat-card hover:shadow-lg transition-shadow cursor-pointer group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Material Management</p>
              <p className="font-heading font-bold text-2xl text-primary group-hover:text-accent transition-colors">{total_materials} Materials</p>
            </div>
            <Package className="w-10 h-10 text-warning" />
          </div>
        </Link>
      </div>

      {progress_data && progress_data.length > 0 && (
        <div className="stat-card mb-8">
          <h3 className="font-heading font-bold text-xl mb-6 uppercase tracking-wide">Activity Progress</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={progress_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="activity" tick={{ fontSize: 12 }} angle={-15} textAnchor="end" height={80} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="planned" fill="#0F172A" name="Planned Quantity" />
              <Bar dataKey="completed" fill="#10B981" name="Completed Quantity" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {recent_dprs && recent_dprs.length > 0 && (
        <div className="stat-card">
          <h3 className="font-heading font-bold text-xl mb-6 uppercase tracking-wide">Recent DPR Entries</h3>
          <div className="space-y-3">
            {recent_dprs.map((dpr) => (
              <div key={dpr.id} className="flex items-center justify-between py-3 border-b border-slate-200 last:border-0">
                <div className="flex-1">
                  <p className="font-medium text-primary">{dpr.activity}</p>
                  <p className="text-sm text-slate-600 font-mono">{dpr.date}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold text-primary">{dpr.actual_quantity} {dpr.unit}</p>
                  <p className="text-xs text-slate-600">Workforce: {dpr.workforce_count}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to={`/dpr-list/${projectId}`} data-testid="view-all-dpr" className="mt-4 inline-block text-sm font-medium text-accent hover:underline">
            View All DPRs →
          </Link>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <Link to={`/workforce/${projectId}`} data-testid="nav-workforce" className="btn-secondary h-auto py-4">
          <Users className="w-5 h-5 mr-2" />
          Workforce Management
        </Link>
        <Link to={`/equipment/${projectId}`} data-testid="nav-equipment" className="btn-secondary h-auto py-4">
          <Truck className="w-5 h-5 mr-2" />
          Equipment Tracking
        </Link>
        <Link to={`/analytics/${projectId}`} data-testid="nav-analytics" className="btn-primary h-auto py-4">
          <Activity className="w-5 h-5 mr-2" />
          Advanced Analytics
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;